import axios from 'axios';

/**
 * Frappe REST API client.
 *
 * CSRF handling strategy:
 *   Frappe enforces CSRF on POST/PUT/DELETE for any "logged-in" session
 *   (anyone with a valid `sid` cookie pointing to a User other than Guest).
 *
 *   Problem: when a user is in Frappe Desk as Administrator (`/app`) and then
 *   navigates to our public `/login` page, the browser still sends the
 *   Administrator `sid` cookie. Frappe's middleware sees a logged-in session,
 *   demands a valid CSRF token, our public seller-login UI doesn't have one,
 *   request gets rejected as "Invalid Request" 400.
 *
 *   Fix: for our public auth endpoints (login, logout, signup, signup_options,
 *   consume_token, whoami) we explicitly DELETE Frappe's session cookies
 *   before issuing the request. Backend then sees us as Guest -> CSRF skipped.
 *
 *   For all OTHER endpoints (post-login, normal seller actions on region site)
 *   we attach the real CSRF token from window.csrf_token if Frappe injected
 *   one into the page.
 */

// Endpoints that should be called as a clean Guest (no Frappe session cookies).
// Anything matching these substrings will trigger pre-call cookie cleanup.
const PUBLIC_AUTH_ENDPOINTS = [
  'marketplace_connector.api.auth.login',
  'marketplace_connector.api.auth.logout',
  'marketplace_connector.api.auth.whoami',
  'marketplace_connector.api.signup.',
  'marketplace_connector.api.platforms.list_active_platforms',
  'marketplace_connector.api.platforms.get_credential_schema',
  'marketplace_connector.api.products.',
  'marketplace_connector.api.mappings.',
  'marketplace_connector.api.product_fields.',
  'marketplace_connector.api.product_tree.',
  'marketplace_connector.api.credentials.',
  'marketplace_connector.api.listings.',
  'marketplace_connector.api.master_data.',
  '/api/method/logout',
];

function isPublicAuthEndpoint(url) {
  return PUBLIC_AUTH_ENDPOINTS.some((needle) => url.includes(needle));
}

function clearFrappeSessionCookies() {
  const cookies = ['sid', 'system_user', 'full_name', 'user_id', 'user_lang'];
  for (const name of cookies) {
    // Multiple delete forms to handle various cookie scopings the browser may have
    document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    document.cookie = `${name}=; path=/; domain=${window.location.hostname}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  }
  // Force sid=Guest as a final guarantee — if any delete didn't take, the
  // Guest sid value won't trigger CSRF protection.
  document.cookie = 'sid=Guest; path=/';
}

const client = axios.create({
  baseURL: '',
  withCredentials: true, // sends Frappe sid cookie on main + mc_session on region
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    // Required on cookie-authenticated writes (CP-B16, utils/portal_csrf.py):
    // another website cannot add this header, so it proves the call came
    // from the portal itself.
    'X-Requested-With': 'XMLHttpRequest',
  },
});

// Pre-flight: clear Frappe session cookies for public auth calls
client.interceptors.request.use((config) => {
  if (isPublicAuthEndpoint(config.url || '')) {
    clearFrappeSessionCookies();
  }

  // Attach the REAL CSRF token only if Frappe injected one
  // (typically only on Frappe Desk / authenticated pages)
  const realCsrf = window.csrf_token;
  if (realCsrf && realCsrf !== 'token') {
    config.headers['X-Frappe-CSRF-Token'] = realCsrf;
  }
  return config;
});

/**
 * Frappe error envelope:
 *   { exception, exc_type, exc, _error_message, _server_messages }
 * `_server_messages` is JSON-encoded list of JSON-encoded objects with `message` key.
 */
function extractFrappeMessage(data) {
  if (data._server_messages) {
    try {
      const arr = JSON.parse(data._server_messages);
      const msgs = arr
        .map((s) => {
          try {
            const obj = typeof s === 'string' ? JSON.parse(s) : s;
            return obj.message || '';
          } catch {
            return s;
          }
        })
        .filter(Boolean);
      if (msgs.length) return msgs.join(' ');
    } catch {
      /* fall through */
    }
  }
  if (data._error_message) return data._error_message;
  if (data.exception) {
    return data.exception
      .replace(/^[a-zA-Z0-9_.]+:\s*/, '')
      .replace(/<[^>]+>/g, '');
  }
  return null;
}

client.interceptors.response.use(
  (resp) => resp,
  (err) => {
    if (err.response?.data) {
      err.frappeException = err.response.data.exception;
      err.frappeMessage = extractFrappeMessage(err.response.data);
    }
    return Promise.reject(err);
  }
);

export function callMethod(dottedPath, payload = {}, { method = 'POST' } = {}) {
  const url = `/api/method/${dottedPath}`;
  if (method === 'GET') {
    return client.get(url, { params: payload }).then((r) => r.data.message);
  }
  return client.post(url, payload).then((r) => r.data.message);
}

export default client;
